import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { DrawerView, ModalView } from "@/constants/views";

interface CommonState {
  runStoreStatusCheck: boolean;
  /**
   * True once the app has finished deciding who the visitor is.
   *
   * Recovered from the original's `common` slice (module 27555). Its purpose is
   * ORDERING, not information: the original sets it inside the same
   * `onAuthStateChanged` callback that installs the axios auth interceptors,
   * immediately after them, so that nothing authenticated fires before the
   * headers exist. Consumers gate on it — the recent-orders rail and the offers
   * provider both do.
   *
   * It is only ever set to `true`, and only on the authenticated branch; signed
   * out it stays `false` for the page's lifetime. Reproduced verbatim.
   */
  isAuthCheckComplete: boolean;
  /**
   * The wallet is refreshing from the customer's latest order.
   *
   * The original raises this after an order settles, because the balances it
   * shows (points, cashback, voucher counts) are recomputed server-side and a
   * claim made against a stale balance would be rejected. While it is set,
   * `walletBusy` disables EVERY claim, unclaim and gift-card Apply, and the
   * header shows the "Refreshing wallet from your latest order" banner.
   */
  walletSyncPending: boolean;
  /** The refresh gave up. Same disabling, plus the Retry button. */
  walletSyncTimedOut: boolean;
  /**
   * Voucher codes granted during this session that are not yet on the stored
   * customer document. The spendable-voucher selector merges them in with
   * `preferExistingCount`, so a code that arrives twice does not double-count.
   */
  sessionVoucherCodes: Record<string, number> | null;
  modal: ModalView | null;
  modalPayload: unknown;
  drawer: DrawerView | null;
  drawerPayload: unknown;
}

const initialState: CommonState = {
  runStoreStatusCheck: false,
  isAuthCheckComplete: false,
  walletSyncPending: false,
  walletSyncTimedOut: false,
  sessionVoucherCodes: null,
  modal: null,
  modalPayload: null,
  drawer: null,
  drawerPayload: null,
};

/** Either `openDrawer('OFFERS_DRAWER')` or `openDrawer({ view, payload })`. */
type ViewInput<V> = V | { view: V; payload?: unknown } | null;

const normalizeView = <V>(
  input: ViewInput<V>,
): { view: V | null; payload: unknown } => {
  if (input == null) return { view: null, payload: null };
  if (typeof input === "string") return { view: input as V, payload: null };
  const object = input as { view: V; payload?: unknown };
  return { view: object.view ?? null, payload: object.payload ?? null };
};

const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    setRunStoreStatusCheck(state, action: PayloadAction<boolean>) {
      state.runStoreStatusCheck = Boolean(action.payload);
    },
    setAuthCheckComplete(state, action: PayloadAction<boolean>) {
      state.isAuthCheckComplete = Boolean(action.payload);
    },
    setWalletSyncPending(state, action: PayloadAction<boolean>) {
      state.walletSyncPending = Boolean(action.payload);
      if (!action.payload) state.walletSyncTimedOut = false;
    },
    setWalletSyncTimedOut(state, action: PayloadAction<boolean>) {
      state.walletSyncTimedOut = Boolean(action.payload);
    },
    /**
     * The header's Retry. The original dispatches it with `Date.now()`; the
     * timestamp is only a change token for the effect that re-runs the sync, so
     * the payload is accepted and ignored here.
     */
    retryWalletSync(state) {
      state.walletSyncTimedOut = false;
      state.walletSyncPending = true;
    },
    setSessionVoucherCodes(
      state,
      action: PayloadAction<Record<string, number> | null>,
    ) {
      state.sessionVoucherCodes = action.payload ?? null;
    },
    openModal(state, action: PayloadAction<ViewInput<ModalView>>) {
      const { view, payload } = normalizeView(action.payload);
      state.modal = view;
      state.modalPayload = payload;
    },
    closeModal(state) {
      state.modal = null;
      state.modalPayload = null;
    },
    openDrawer(state, action: PayloadAction<ViewInput<DrawerView>>) {
      const { view, payload } = normalizeView(action.payload);
      state.drawer = view;
      state.drawerPayload = payload;
    },
    closeDrawer(state) {
      state.drawer = null;
      state.drawerPayload = null;
    },
  },
});

export const {
  setRunStoreStatusCheck,
  setAuthCheckComplete,
  setWalletSyncPending,
  setWalletSyncTimedOut,
  retryWalletSync,
  setSessionVoucherCodes,
  openModal,
  closeModal,
  openDrawer,
  closeDrawer,
} = commonSlice.actions;

export default commonSlice.reducer;
